import { createBrowserRouter } from 'react-router-dom';
import React from 'react';

import {
  ACCOUNT_MANAGER,
  ADMIN,
  LINK_TO_CHANNELS,
  LINK_TO_CREATE_ORDER,
  LINK_TO_DICTIONARY,
  LINK_TO_MAIN,
  LINK_TO_ORDER_INFO,
  LINK_TO_ORDERS,
  LINK_TO_STATISTIC,
  SUPER_ADMIN,
} from './../constants';

import { useAppSelector } from './useRedux';

import { SidebarLayout } from 'components/layouts/SidebarLayout';
import { CLIENT } from 'features/constants';
import { selectAuth } from 'features/store/slices/auth';
import { CheckAuthorization } from 'components/layouts/check-authorization';

const routers = {
  telegram: {
    MainPage: React.lazy(() => import('../../pages/Telegram/MainPage')),
    CreateOrderPage: React.lazy(() => import('../../pages/Telegram/CreateOrderPage')),
    OrdersPage: React.lazy(() => import('../../pages/Telegram/OrdersPage')),
    OrderInfoPage: React.lazy(() => import('../../pages/Telegram/OrderInfoPage')),
    OrderStatisticsPage: React.lazy(() => import('../../pages/Telegram/OrderStatisticsPage')),
    ChannelsPage: React.lazy(() => import('../../pages/Telegram/ChannelsPage')),
    MarginReportPage: React.lazy(() => import('../../pages/Telegram/Reports/MarginReportPage')),
    PaymentConfirmationsPage: React.lazy(() => import('../../pages/Telegram/Admin/PaymentConfirmationsPage')),
    ReportAccountingPage: React.lazy(() => import('../../pages/Telegram/Reports/ReportAccoutingPage')),
    DictionaryPage: React.lazy(() => import('../../pages/Telegram/DictionaryPage')),
    PriceCalcPage: React.lazy(() => import('../../pages/Telegram/PriceCalculationPage')),
    PricesPage: React.lazy(() => import('../../pages/Telegram/Reports/PricesPage')),
    RoleManagementPage: React.lazy(() => import('../../pages/Telegram/Admin/RoleManagementPage')),
    ChannelsStatisticsPage: React.lazy(() => import('../../pages/Telegram/Reports/ChannelsStatisticsPage')),
    ReportOnBuyersPage: React.lazy(() => import('../../pages/Telegram/Reports/ReportOnBuyersPage')),
    TimeOfPlacementPage: React.lazy(() => import('../../pages/Telegram/Admin/TimeOfPlacementPage')),
    PersonalReportOnBuyerPage: React.lazy(() => import('../../pages/Telegram/Reports/PersonalRepostOnBuyer')),
    ClientsPage: React.lazy(() => import('../../pages/Telegram/ClientsPage')),
    CampaignsPage: React.lazy(() => import('../../pages/Telegram/CampaignsPage')),
  },
  instagram: {
    MainPage: React.lazy(() => import('../../pages/Instagram/Main')),
    OrdersPage: React.lazy(() => import('../../pages/Instagram/Orders')),
    BloggersPage: React.lazy(() => import('../../pages/Instagram/Bloggers')),
    ClientsPage: React.lazy(() => import('../../pages/Instagram/Clients')),
    OrderReportPage: React.lazy(() => import('../../pages/Instagram/ReportOrder')),
    ClientInfoPage: React.lazy(() => import('../../pages/Instagram/ClientInfo')),
    // VerifyBloggerPage: React.lazy(() => import('../../pages/Instagram/VerifyBlogger')),
    CreateInstagramOrder: React.lazy(() => import('../../pages/Instagram/CreateOrder')),
    EditOrderPage: React.lazy(() => import('../../pages/Instagram/EditOrder')),
  },
  common: {
    NotFoundPage: React.lazy(() => import('../../pages/NotFoundPage')),
    LegalPage: React.lazy(() => import('../../pages/LegalPage')),
    PrivacyPage: React.lazy(() => import('../../pages/PrivacyPage')),
    DeletePage: React.lazy(() => import('../../pages/DeletePage')),
  },
};

export const useRouter = () => {
  const { user } = useAppSelector(selectAuth);

  const instagramRoutes = [
    {
      path: '/instagram',
      element: <routers.instagram.MainPage />,
    },
    {
      path: '/instagram/orders',
      element: <routers.instagram.OrdersPage />,
    },
    {
      path: '/instagram/bloggers',
      element: <routers.instagram.BloggersPage />,
    },
    {
      path: '/instagram/clients',
      element: <routers.instagram.ClientsPage />,
    },
    {
      path: '/instagram/orders/:id/report',
      element: <routers.instagram.OrderReportPage />,
    },
    {
      path: '/instagram/clients/:id',
      element: <routers.instagram.ClientInfoPage />,
    },
    // {
    //   path: '/instagram/bloggers/verify/:id',
    //   element: <routers.instagram.VerifyBloggerPage />,
    // },
    {
      path: '/instagram/orders/create/:id',
      element: <routers.instagram.CreateInstagramOrder />,
    },
    {
      path: '/instagram/order/edit/:id',
      element: <routers.instagram.EditOrderPage />,
    },
  ];

  if (user?.roleId === CLIENT) {
    return createBrowserRouter([
      {
        element: <SidebarLayout />,
        children: [
          {
            element: <CheckAuthorization />,
            children: [
              {
                path: '/',
                element: <routers.telegram.ClientsPage />,
              },
              {
                path: '/campaigns/:id',
                element: <routers.telegram.CampaignsPage />,
              },
              ...instagramRoutes,
            ],
          },
          {
            path: LINK_TO_STATISTIC,
            element: <routers.telegram.OrderStatisticsPage />,
          },
        ],
      },
      {
        path: '*',
        element: <routers.common.NotFoundPage />,
      },
    ]);
  } else {
    const privateRoutes = [
      {
        path: LINK_TO_MAIN,
        element: <routers.telegram.MainPage />,
      },
      {
        path: LINK_TO_CREATE_ORDER,
        element: <routers.telegram.CreateOrderPage />,
      },
      {
        path: LINK_TO_ORDERS,
        element: <routers.telegram.OrdersPage />,
      },
      {
        path: LINK_TO_ORDER_INFO,
        element: <routers.telegram.OrderInfoPage />,
      },
      {
        path: LINK_TO_CHANNELS,
        element: <routers.telegram.ChannelsPage />,
      },
      {
        path: '/pricing',
        element: <routers.telegram.PriceCalcPage />,
      },
      {
        path: '/reports/pricing',
        element: <routers.telegram.PricesPage />,
      },
      {
        path: '/admin/roles',
        element: <routers.telegram.RoleManagementPage />,
      },
      {
        path: '/reports/margin',
        element: <routers.telegram.MarginReportPage />,
      },
      {
        path: '/reports/accounting',
        element: <routers.telegram.ReportAccountingPage />,
      },
      {
        path: '/admin/payments',
        element: <routers.telegram.PaymentConfirmationsPage />,
      },
      {
        path: '/reports/channels',
        element: <routers.telegram.ChannelsStatisticsPage />,
      },
      {
        path: '/reports/buyers',
        element: <routers.telegram.ReportOnBuyersPage />,
      },
      {
        path: `/reports/buyers/:id`,
        element: <routers.telegram.PersonalReportOnBuyerPage />,
      },
      {
        path: LINK_TO_DICTIONARY,
        element: <routers.telegram.DictionaryPage />,
      },
      {
        path: '/admin/schedule',
        element: <routers.telegram.TimeOfPlacementPage />,
      },

      ...instagramRoutes,
    ];

    const routes = [
      {
        element: <CheckAuthorization />,
        children: privateRoutes,
      },

      {
        path: LINK_TO_STATISTIC,
        element: <routers.telegram.OrderStatisticsPage />,
      },
    ];

    // Пример добавления маршрута по условию
    if (user?.roleId === ADMIN || user?.roleId === ACCOUNT_MANAGER || user?.roleId === SUPER_ADMIN) {
      privateRoutes.push(
        ...[
          {
            path: '/clients',
            element: <routers.telegram.ClientsPage />,
          },
          {
            path: '/campaigns/:id',
            element: <routers.telegram.CampaignsPage />,
          },
        ],
      );
    }

    return createBrowserRouter([
      {
        element: <SidebarLayout />,
        children: routes,
      },
      {
        path: '/privacy',
        element: <routers.common.PrivacyPage />,
      },
      {
        path: '/legal',
        element: <routers.common.LegalPage />,
      },
      {
        path: '/remove-guide',
        element: <routers.common.DeletePage />,
      },
      {
        path: '*',
        element: <routers.common.NotFoundPage />,
      },
    ]);
  }
};
